import React from 'react';
import Icon from '../../../components/AppIcon';

const ReadinessScore = ({ score, recommendedSavings, yearsToRetirement }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return { bg: 'bg-success', text: 'text-success', label: 'Excellent' };
    if (score >= 60) return { bg: 'bg-primary', text: 'text-primary', label: 'Good' };
    if (score >= 40) return { bg: 'bg-warning', text: 'text-warning', label: 'Fair' };
    return { bg: 'bg-error', text: 'text-error', label: 'Needs Improvement' };
  };

  const scoreColor = getScoreColor(score);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="bg-card rounded-xl p-6 border shadow-sm">
      <h2 className="text-xl font-semibold text-foreground mb-6">Retirement Readiness Assessment</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Score Circle */}
        <div className="flex flex-col items-center justify-center">
          <div className="relative w-32 h-32">
            <svg className="transform -rotate-90 w-32 h-32">
              <circle
                cx="64"
                cy="64"
                r="45"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-muted"
              />
              <circle
                cx="64"
                cy="64"
                r="45"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className={scoreColor?.text}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-foreground">{score?.toFixed(0)}%</span>
              <span className={`text-xs font-medium ${scoreColor?.text}`}>{scoreColor?.label}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4 text-center">Readiness Score</p>
        </div>

        {/* Recommended Savings */}
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <Icon name="TrendingUp" size={20} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Recommended Monthly Savings</p>
              <p className="text-2xl font-bold text-foreground">
                ${recommendedSavings?.toFixed(2)}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Additional monthly savings needed to reach your retirement goals
          </p>
        </div>

        {/* Years to Retirement */}
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-warning/10">
              <Icon name="Calendar" size={20} color="var(--color-warning)" strokeWidth={2} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Years to Retirement</p>
              <p className="text-2xl font-bold text-foreground">{yearsToRetirement}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Time remaining to build your retirement savings
          </p>
        </div>
      </div>

      {/* Action Items */}
      {score < 80 && (
        <div className="mt-6 pt-6 border-t">
          <h3 className="text-sm font-semibold text-foreground mb-3">Action Items to Improve Readiness</h3>
          <div className="space-y-2">
            {recommendedSavings > 0 && (
              <div className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Increase monthly savings by ${recommendedSavings?.toFixed(2)} to close the income gap
                </p>
              </div>
            )}
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Maximize RRSP contributions for tax advantages
              </p>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Consider delaying retirement by 1-2 years to increase savings
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadinessScore;